import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { FileText, FolderOpen, type LucideIcon } from "lucide-react";

interface EmptyStateAction {
  label: string;
  onClick: () => void;
  /** Leading icon, e.g. `Plus` for "New {Pilot} Job" CTAs. */
  icon?: LucideIcon;
}

interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description: string;
  action?: EmptyStateAction;
  className?: string;
}

export function EmptyState({
  icon: Icon = FileText,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-card/50 p-10 text-center",
        className
      )}
      role="status"
      aria-label={title}
    >
      <div className="mx-auto flex size-16 items-center justify-center rounded-xl border-2 border-muted bg-muted/50">
        <Icon className="size-8 text-muted-foreground" aria-hidden="true" />
      </div>
      <h3 className="mt-5 text-lg font-bold text-foreground">{title}</h3>
      <p className="mt-2 max-w-sm text-sm text-muted-foreground leading-relaxed">
        {description}
      </p>
      {action && (
        <Button onClick={action.onClick} className="mt-6" size="lg">
          {action.icon ? <action.icon aria-hidden="true" /> : null}
          {action.label}
        </Button>
      )}
    </div>
  );
}

/**
 * Pre-configured empty state for projects
 */
export function EmptyProjectsState({
  onCreateNew,
}: {
  onCreateNew?: () => void;
}) {
  return (
    <EmptyState
      icon={FolderOpen}
      title="No projects yet"
      description="Create a project to organize your submittals and keep everything in one place."
      action={
        onCreateNew
          ? { label: "New Project", onClick: onCreateNew }
          : undefined
      }
    />
  );
}

/*
 * Usage Example:
 *
 * import { EmptyState } from "@/components/empty-state";
 *
 * // Pilot job lists get their empty state from `JobListPage` (`@/components/layout`).
 * function SearchResults() {
 *   return (
 *     <EmptyState
 *       icon={FileX}
 *       title="No results found"
 *       description="Try adjusting your search criteria"
 *     />
 *   );
 * }
 *
 * Accessibility:
 * - Uses role="status" for screen readers
 * - Icon has aria-hidden
 * - Title is accessible via aria-label
 */
